import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import {
  MarkdownContent,
  type MarkdownLinkClickHandler,
} from "@/components/ui/markdown-content";
import type { ConversationMessage } from "@/stores/conversation-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { summarizeDesktopInteractionReceipt } from "@/desktop/desktop-interaction-receipt";
import { translate, useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { renderActivityLabel } from "@/lib/activity-label";
import { ConversationInlineQuestionCard } from "./conversation-inline-question-card";

interface ConversationExecutionStepsProps {
  executionId: string;
  steps: readonly ConversationMessage[];
  active: boolean;
  startedAt?: string;
  completedAt?: string;
  onLinkClick: MarkdownLinkClickHandler;
  testId: string;
}

function stepLabel(step: ConversationMessage): string {
  return (
    renderActivityLabel(
      step.contentLabel,
      step.content.replace(/^Tool:\s*/i, "").trim(),
    ) || translate(framesConversationMessages, "toolCall")
  );
}

function DesktopInteractionStep({ step }: { step: ConversationMessage }) {
  const t = useT(framesConversationMessages);
  const summary = summarizeDesktopInteractionReceipt(step.desktopReceipt);
  return (
    <div className="min-w-0 break-words">
      <div>{step.content || t("desktopInteraction")}</div>
      {summary ? (
        <div
          className="mt-0.5 text-appkit-mini leading-relaxed"
          data-testid={`desktop-receipt-${step.id}`}
        >
          {[
            summary.action,
            summary.result,
            summary.capturedAfter ? t("screenshotTakenAfter") : undefined,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      ) : null}
    </div>
  );
}

function formatWorkedDuration(
  startedAt?: string,
  completedAt?: string,
): string {
  const worked = translate(framesConversationMessages, "worked");
  if (!startedAt) return worked;
  const start = Date.parse(startedAt);
  const end = completedAt ? Date.parse(completedAt) : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return worked;
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const unit = (
    key: "durationHours" | "durationMinutes" | "durationSeconds",
    count: number,
  ) => translate(framesConversationMessages, key, { count });
  const parts = [];
  if (hours > 0) parts.push(unit("durationHours", hours));
  if (minutes % 60 > 0) parts.push(unit("durationMinutes", minutes % 60));
  parts.push(unit("durationSeconds", seconds % 60));
  return translate(framesConversationMessages, "workedFor", {
    duration: parts.join(" "),
  });
}

export function ConversationExecutionSteps({
  executionId,
  steps,
  active,
  startedAt,
  completedAt,
  onLinkClick,
  testId,
}: ConversationExecutionStepsProps) {
  const t = useT(framesConversationMessages);
  const [expanded, setExpanded] = useState(active);
  const [, setClockTick] = useState(0);
  const wasActiveRef = useRef(active);
  const panelId = `${testId}-steps-${executionId}-panel`;
  const pendingQuestionCursor = useCanvasStore((s) =>
    [...s.agentCursors.values()].find(
      (cursor) => cursor.executionId === executionId && cursor.question,
    ),
  );

  useEffect(() => {
    if (active) {
      setExpanded(true);
    } else if (wasActiveRef.current) {
      setExpanded(false);
    }
    wasActiveRef.current = active;
  }, [active]);

  useEffect(() => {
    if (!active || !startedAt) return undefined;
    const timer = window.setInterval(
      () => setClockTick((value) => value + 1),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [active, startedAt]);

  return (
    <div
      className="w-full max-w-[92%] rounded-control border border-glass-stroke-faint"
      data-testid={`${testId}-steps-${executionId}`}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto w-full justify-start rounded-control px-2.5 py-1.5 text-canvas-xs text-muted hover:text-secondary"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((value) => !value)}
        data-testid={`${testId}-steps-${executionId}-toggle`}
      >
        {expanded ? (
          <Icons.ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
        ) : (
          <Icons.ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
        )}
        <span>
          {active
            ? `${formatWorkedDuration(startedAt)}…`
            : formatWorkedDuration(
                completedAt ? startedAt : undefined,
                completedAt,
              )}
        </span>
      </Button>

      {expanded ? (
        <div
          id={panelId}
          className="space-y-1 border-t border-glass-stroke-faint px-2.5 py-2 text-canvas-xs text-muted"
          data-testid={`${testId}-steps-${executionId}-panel`}
        >
          {steps.map((step) => (
            <div
              key={step.id}
              className="flex items-start gap-2"
              data-testid={`${testId}-step-${step.id}`}
            >
              {step.activityKind === "desktop_interaction" ? (
                <Icons.Monitor
                  className="mt-px h-3 w-3 shrink-0"
                  aria-hidden="true"
                />
              ) : step.activityKind === "tool_call" ? (
                <Icons.Wrench
                  className="mt-px h-3 w-3 shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <Icons.Lightbulb
                  className="mt-px h-3 w-3 shrink-0"
                  aria-hidden="true"
                />
              )}
              {step.activityKind === "desktop_interaction" ? (
                <DesktopInteractionStep step={step} />
              ) : step.activityKind === "tool_call" ? (
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="min-w-0 flex-1 break-words">
                    {stepLabel(step)}
                  </span>
                  {step.toolFailed ? (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-danger"
                      title={t("toolFailed")}
                      aria-label={t("toolFailed")}
                      data-testid={`${testId}-step-${step.id}-failed-dot`}
                    />
                  ) : null}
                </span>
              ) : (
                <MarkdownContent
                  content={step.content}
                  onLinkClick={onLinkClick}
                  className="min-w-0 text-canvas-xs text-muted [&_p]:leading-relaxed"
                />
              )}
            </div>
          ))}
          {pendingQuestionCursor?.question ? (
            <ConversationInlineQuestionCard
              question={pendingQuestionCursor.question}
              color={pendingQuestionCursor.color}
              testId={`${testId}-inline-question-${executionId}`}
              appearance="embed"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
